import { ICompanyMemberPure } from './Company'

export enum EvaluationStatus {
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  // ONLY_LEAD_SUBMITTED,ONLY_MEMBER_SUBMITTED to be removed
  ONLY_LEAD_SUBMITTED = 'only_lead_submitted',
  ONLY_MEMBER_SUBMITTED = 'only_member_submitted',
  DONE = 'done',
}

export enum SingleEvaluationStatus {
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  NOT_COMPLETE_IN_TIME = 'not_complete_in_time',
  DONE = 'done',
}

// Until phase 3 this is used for emails only
export enum EvaluationType {
  SKILL = 'skill',
  GOAL = 'goal',
  CULTURE = 'culture',
}

export type EvaluationAssessmentType = 'skills' | 'goals' | 'culture'

// Per-(section, viewer) reason WHY a section is empty/hidden, computed at runtime
export enum RevealReasonType {
  // Hidden until this viewer submits their own self-assessment
  hiddenUntilSelfAssess = 'hidden_until_self_assess',
  // Hidden until every assessor of the section has submitted
  hiddenUntilComplete = 'hidden_until_complete',
  // Hidden until an admin manually reveals the assessments
  hiddenManual = 'hidden_manual',
  // A visitor cannot see a section another assessor has not submitted yet
  notSubmitted = 'not_submitted',
  // No assessors configured for the section / member has nothing to assess
  notAssessed = 'not_assessed',
}

export interface IEvaluationGoalReview {
  goalId: string
  fields: {
    goalStatus: number
    reasonText: string
  }
}

export interface IEvaluationSkillReview {
  skillId: string
  levelId?: string
  fields: {
    expectationValue: number
    cannotScoreNow: boolean
    reasonText?: string
  }
}

export interface IEvaluationCultureReview {
  habitId: string
  alignmentValue: number
  textFeedback: string
}

export type ISingleEvaluationCreateRequest = Pick<
  ISingleEvaluationPure,
  'skills' | 'goals' | 'skillsSubmitted' | 'goalsSubmitted'
>

export type ISingleEvaluationSubmissionStatus = Pick<
  ISingleEvaluationPure,
  'skillsSubmitted' | 'goalsSubmitted' | 'cultureSubmitted'
>

// Maps an assessment type to its submission-timestamp field on a single
// evaluation. Shared by the member stat/collection views and the evaluation
// modal so the mapping lives in one place.
export const SUBMITTED_KEY: Record<
  EvaluationAssessmentType,
  keyof ISingleEvaluationSubmissionStatus
> = {
  skills: 'skillsSubmitted',
  goals: 'goalsSubmitted',
  culture: 'cultureSubmitted',
}

export interface ISingleEvaluationPure {
  _id?: any
  status: SingleEvaluationStatus
  senderId: string
  dateSubmitted: Date
  skillsSubmitted?: Date
  goalsSubmitted?: Date
  cultureSubmitted?: Date
  skills: IEvaluationSkillReview[]
  goals: IEvaluationGoalReview[]
  culture: IEvaluationCultureReview[]
}
export interface IEvaluationPure {
  _id?: any
  dateCreated: Date
  termNumber: number
  status: EvaluationStatus
  memberId: string
  companyId: string
  companyVersion: number
  skipped?: boolean
  dateSkipped?: Date
  assessment: ISingleEvaluationPure[]
  assessors: IEvaluationAssessor[]
  hideAssessments: {
    self: boolean
    visitor: boolean
  }
}

export type ICreateEvaluationInDb = Pick<
  IEvaluationPure,
  | 'memberId'
  | 'termNumber'
  | 'assessors'
  | 'companyId'
  | 'assessment'
  | 'companyVersion'
>

export interface IEvaluationState {
  _id?: any
  memberId: string
  status: EvaluationStatus
  termNumber: number
  assessors: IEvaluationAssessor[]
  assessment: ISingleEvaluationPure[]
  skipped?: boolean
  leadId: string
  isMemberRequired: boolean
  companyVersion: number
  hideAssessments: {
    self: boolean
    visitor: boolean
  }
}

interface IEvaluationAssessorBase {
  goals?: boolean
  skills?: boolean
  culture?: boolean
}

export interface IEvaluationAssessor extends IEvaluationAssessorBase {
  memberId: string
}

export interface IEvaluationAssessorAsCompanyMemberPure
  extends IEvaluationAssessorBase {
  member: ICompanyMemberPure
}

// What reopening a skipped round recreated, and what finalising its planning would
// put at risk in later terms
export interface IReopenSkippedRoundSummary {
  termNumber: number
  leftoversDeleted: number
  evaluationsCreated: number
  withGoals: number
  withSkills: number
  // Active admins with an open planning request once the round is reopened
  adminsWithPlanRequest: number
  atRisk: { pdpsWithReviews: number; reviewRequests: number }
  applied?: boolean
}

export interface IEvaluationAssessorsUpdateRequest {
  goalAssessorIds: string[]
  skillAssessorIds: string[]
  cultureAssessorIds: string[]
}

export interface IEvaluationCultureResultAggregation {
  memberId: string
  external: boolean
  results: { [key: string]: number }
}

export interface IEvaluationCultureInsightsAggregation {
  memberId: string
  avgCultureResult: number
  memberCultureEvaluations: number
}

export interface IEvaluationCultureOverview {
  memberId: string
  assessments: IEvaluationCulturePrivacyBasics[]
}

type ISingleEvaluationPrivacyBasics = Pick<
  ISingleEvaluationPure,
  '_id' | 'senderId' | 'status'
>
export interface IEvaluationCulturePrivacyBasics
  extends ISingleEvaluationPrivacyBasics {
  memberId: string
}

export const evaluationSkillScoreConstraints = {
  min: -1,
  max: 100,
  cannotScoreValue: -1,
} as const

export const evaluationCultureScoreConstraints = {
  min: -1,
  max: 100,
} as const
